import DOMPurify from 'dompurify';
import '../assets/css/osp-frontend.css';

/**
 * Generate random CSS classes with prefix to inject into rendered rows and cards
 */
function generateRandomRowClasses() {
  const r1 = Math.random().toString(36).substring(2, 7);
  const r2 = Math.random().toString(36).substring(2, 7);
  return `osp-r-${r1} ops-row-${r2}`;
}

/**
 * Helper to slugify text for CSS class names
 */
function slugify(text) {
  if (!text) return 'plan';
  return text
    .toString()
    .toLowerCase()
    .replace(/\s+/g, '-')
    .replace(/[^\w\-]+/g, '')
    .replace(/\-\-+/g, '-')
    .replace(/^-+/, '')
    .replace(/-+$/, '');
}

/**
 * Check if client is likely from India (via trace country or timezone)
 */
function isIndiaClient(traceCountry) {
  const tz = typeof Intl !== 'undefined' && Intl.DateTimeFormat ? Intl.DateTimeFormat().resolvedOptions().timeZone : '';
  return traceCountry === 'India' || tz === 'Asia/Kolkata' || tz === 'Asia/Calcutta';
}

/**
 * Currency state & management
 */
function getActiveCurrency(currencyInfo) {
  const stored = localStorage.getItem('osp_currency');
  if (stored) return stored.toLowerCase();
  if (currencyInfo && isIndiaClient(currencyInfo.trace_country)) {
    return 'inr';
  }
  return 'usd';
}

function setActiveCurrency(curr) {
  const normalized = (curr || 'usd').toLowerCase();
  localStorage.setItem('osp_currency', normalized);

  document.body.classList.remove('osp-currency-usd', 'osp-currency-inr', 'osp-currency-eur');
  document.body.classList.add(`osp-currency-${normalized}`);

  window.dispatchEvent(new CustomEvent('ospCurrencyChange', { detail: normalized }));
}

function formatPrice(usdPrice, currencyInfo, activeCurrency) {
  const priceVal = parseFloat(usdPrice);
  if (isNaN(priceVal) || priceVal <= 0) return '-';

  const curr = (activeCurrency || 'usd').toLowerCase();
  if (curr === 'inr' && currencyInfo && currencyInfo.inr_rate) {
    return `₹${Math.round(priceVal * currencyInfo.inr_rate)}`;
  } else if (curr === 'eur' && currencyInfo && currencyInfo.euro_rate) {
    return `€${(priceVal * currencyInfo.euro_rate).toFixed(2)}`;
  }
  return `$${priceVal.toFixed(2)}`;
}

/**
 * Helper to create Currency Switcher HTML & attach handlers
 */
function createCurrencySwitcherElement(traceCountry, onCurrencyChange) {
  if (!isIndiaClient(traceCountry)) {
    return null;
  }

  const container = document.createElement('div');
  container.className = 'osp-layout-switcher osp-currency-switcher';
  container.style.cssText = 'display: inline-flex; gap: 2px;';

  const currencies = ['inr', 'usd', 'eur'];
  const updateButtons = (activeCurr) => {
    container.innerHTML = '';
    currencies.forEach(curr => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `osp-layout-btn ${activeCurr === curr ? 'active' : ''}`;
      btn.textContent = curr.toUpperCase();
      btn.style.cssText = 'font-size: 12px; font-weight: 700; padding: 6px 10px; height: 34px;';
      btn.addEventListener('click', (e) => {
        e.preventDefault();
        setActiveCurrency(curr);
        if (onCurrencyChange) onCurrencyChange(curr);
      });
      container.appendChild(btn);
    });
  };

  const initialCurr = getActiveCurrency({ trace_country: traceCountry });
  updateButtons(initialCurr);

  const currencyHandler = (e) => {
    if (e.detail) {
      updateButtons(e.detail);
    }
  };
  window.addEventListener('ospCurrencyChange', currencyHandler);

  return container;
}

/**
 * Render Currency Switcher mount point
 */
function renderCurrencySwitcherApp(el) {
  const traceIp = el.getAttribute('data-trace-ip') || '';
  el.innerHTML = '';
  const switcher = createCurrencySwitcherElement(traceIp);
  if (switcher) {
    el.appendChild(switcher);
  }
}

/**
 * Render VPS Plans using Custom Vanilla JS
 */
function renderVpsApp(el) {
  let plans = [];
  try {
    plans = JSON.parse(el.getAttribute('data-plans') || '[]');
  } catch (e) {
    console.error('Failed to parse VPS plans', e);
  }

  let currencyInfo = { inr_rate: 0, euro_rate: 0, show_currency: '1', trace_country: '' };
  try {
    const parsed = JSON.parse(el.getAttribute('data-currency-info') || 'null');
    if (parsed) currencyInfo = parsed;
  } catch (e) {
    console.error('Failed to parse VPS currencyInfo', e);
  }

  const searchEnabled = (el.getAttribute('data-search-enabled') || '').toLowerCase() !== 'false';
  const buttonText = el.getAttribute('data-button-text') || 'Configure';
  const initialLayout = (el.getAttribute('data-layout') || 'table').toLowerCase();

  let layoutMode = initialLayout === 'box' ? 'box' : 'list';
  let searchQuery = '';
  let showSearch = false;
  let sortField = 'name';
  let sortOrder = 'asc';
  let isMobile = window.innerWidth <= 768;
  let activeCurrency = getActiveCurrency(currencyInfo);

  const pluginUrl = window.ospFrontendData?.pluginUrl || '';

  const render = () => {
    el.innerHTML = '';

    const rootDiv = document.createElement('div');
    rootDiv.id = 'osp-sheet';
    rootDiv.className = 'osp-responsive-container f32';

    // Controls bar
    const controlsBar = document.createElement('div');
    controlsBar.className = 'osp-controls-bar';

    // Search Toggle Container
    if (searchEnabled) {
      const searchContainer = document.createElement('div');
      searchContainer.className = 'osp-search-toggle-container';
      searchContainer.style.cssText = 'display: flex; alignItems: center; gap: 8px;';

      if (showSearch) {
        const searchBox = document.createElement('div');
        searchBox.style.cssText = 'display: flex; align-items: center; gap: 8px;';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'osp-search-input';
        input.placeholder = 'Search server location ...';
        input.value = searchQuery;
        input.style.cssText = 'width: 200px; padding: 6px 10px; font-size: 13px;';
        input.addEventListener('input', (e) => {
          searchQuery = e.target.value;
          renderListContent();
        });

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'osp-search-toggle-btn';
        closeBtn.style.cssText = 'background: transparent; border: none; cursor: pointer; color: #6c757d; padding: 4px; display: flex; align-items: center;';
        closeBtn.title = 'Close Search';
        closeBtn.innerHTML = '<span class="dashicons dashicons-no-alt" style="font-size: 18px; width: 18px; height: 18px;"></span>';
        closeBtn.addEventListener('click', () => {
          showSearch = false;
          searchQuery = '';
          render();
        });

        searchBox.appendChild(input);
        searchBox.appendChild(closeBtn);
        searchContainer.appendChild(searchBox);
        setTimeout(() => input.focus(), 0);
      } else {
        const toggleBtn = document.createElement('button');
        toggleBtn.type = 'button';
        toggleBtn.className = 'osp-search-toggle-btn';
        toggleBtn.title = 'Search';
        toggleBtn.style.cssText = 'background: #ffffff; border: 1px solid #ccd0d4; border-radius: 6px; cursor: pointer; padding: 6px 10px; display: inline-flex; align-items: center; justify-content: center; transition: all 0.15s ease-in-out; height: 34px; width: 34px;';
        toggleBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width: 16px; height: 16px; display: block; color: #50575e;">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        `;
        toggleBtn.addEventListener('click', () => {
          showSearch = true;
          render();
        });
        searchContainer.appendChild(toggleBtn);
      }
      controlsBar.appendChild(searchContainer);
    }

    // Right Controls
    const controlsRight = document.createElement('div');
    controlsRight.className = 'osp-controls-right';
    controlsRight.style.cssText = 'display: flex; align-items: center; gap: 15px;';

    if (!isMobile) {
      const layoutSwitcher = document.createElement('div');
      layoutSwitcher.className = 'osp-layout-switcher';

      const listBtn = document.createElement('button');
      listBtn.type = 'button';
      listBtn.className = `osp-layout-btn ${layoutMode === 'list' || layoutMode === 'table' ? 'active' : ''}`;
      listBtn.title = 'List View';
      listBtn.innerHTML = '<span class="dashicons dashicons-list-view"></span>';
      listBtn.addEventListener('click', () => {
        layoutMode = 'list';
        render();
      });

      const boxBtn = document.createElement('button');
      boxBtn.type = 'button';
      boxBtn.className = `osp-layout-btn ${layoutMode === 'box' ? 'active' : ''}`;
      boxBtn.title = 'Grid View';
      boxBtn.innerHTML = '<span class="dashicons dashicons-grid-view"></span>';
      boxBtn.addEventListener('click', () => {
        layoutMode = 'box';
        render();
      });

      layoutSwitcher.appendChild(listBtn);
      layoutSwitcher.appendChild(boxBtn);
      controlsRight.appendChild(layoutSwitcher);
    }

    const switcherEl = createCurrencySwitcherElement(currencyInfo.trace_country, (curr) => {
      activeCurrency = curr;
      renderListContent();
    });
    if (switcherEl) {
      controlsRight.appendChild(switcherEl);
    }

    controlsBar.appendChild(controlsRight);
    rootDiv.appendChild(controlsBar);

    // Container for dynamic list/box plans
    const contentContainer = document.createElement('div');
    contentContainer.className = 'osp-plans-content-area';
    rootDiv.appendChild(contentContainer);

    el.appendChild(rootDiv);

    renderListContent();
  };

  const getFilteredAndSortedPlans = () => {
    const q = searchQuery.toLowerCase().trim();
    const filtered = plans.filter(p => {
      if (!searchEnabled || !q) return true;
      return (
        (p.name || '').toLowerCase().includes(q) ||
        (p.location || '').toLowerCase().includes(q) ||
        (p.core || '').toLowerCase().includes(q) ||
        (p.ram || '').toLowerCase().includes(q) ||
        (p.harddisk || '').toLowerCase().includes(q) ||
        (p.bandwidth || '').toLowerCase().includes(q)
      );
    });

    return [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'monthly_price') {
        aVal = parseFloat(a.monthly_price) || 0;
        bVal = parseFloat(b.monthly_price) || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (sortField === 'offer_price') {
        aVal = parseFloat(a.best_offer?.offer_price || a.monthly_price) || 0;
        bVal = parseFloat(b.best_offer?.offer_price || b.monthly_price) || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else {
        const strA = String(aVal || '').replace(/[–—]/g, '-');
        const strB = String(bVal || '').replace(/[–—]/g, '-');
        const cmp = strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' });
        return sortOrder === 'asc' ? cmp : -cmp;
      }
    });
  };

  const renderListContent = () => {
    const contentArea = el.querySelector('.osp-plans-content-area');
    if (!contentArea) return;
    contentArea.innerHTML = '';

    const currentPlans = getFilteredAndSortedPlans();
    const displayAsBox = isMobile || layoutMode === 'box';

    if (currentPlans.length === 0) {
      contentArea.innerHTML = '<div style="padding: 20px; text-align: center; background: #fff; border: 1px solid #dee2e6; border-radius: 8px;">Nothing Found !</div>';
      return;
    }

    if (displayAsBox) {
      const cardsGrid = document.createElement('div');
      cardsGrid.className = 'osp-cards-view osp-vps-cards-grid f32';

      currentPlans.forEach(plan => {
        const hasOffer = plan.best_offer?.savings > 1 && plan.best_offer?.offer_price > 0;
        const planSlug = slugify(plan.name);
        const randomClasses = generateRandomRowClasses();

        const card = document.createElement('div');
        card.className = `osp-card-item osp-card-pid-${plan.pid} osp-card-name-${planSlug} ${randomClasses}`;

        const nameParts = (plan.name || '').split(/\s*[-–—]\s*/);
        const name1 = nameParts[0] || '';
        const name2 = nameParts[1] || '';

        const flagHtml = plan.flag_class ? `<i class="flag ${plan.flag_class}" style="flex-shrink: 0; margin: 0;"></i>` : '';
        const nameSubHtml = name2 ? `<span style="font-size: 11px; color: #6c757d; font-weight: normal; margin-top: 2px; line-height: 1.1;">${name2.trim()}</span>` : '';

        const offerHtml = hasOffer ? `
          <div class="osp-card-row">
            <span class="osp-card-label">Offer Price</span>
            <span class="osp-card-value osp-card-value-bold" style="color: #28a745;">
              ${formatPrice(plan.best_offer.offer_price, currencyInfo, activeCurrency)}/mo
              <span class="osp-savings-badge">Save ${plan.best_offer.savings}%</span>
            </span>
          </div>
        ` : '';

        card.innerHTML = `
          <div class="osp-card-row" style="border-bottom: 2px solid #dee2e6; padding-bottom: 12px;">
            <a href="${plan.buy_url || '#'}" class="osp-flag-link" style="display: flex; align-items: center; gap: 8px; text-decoration: none; line-height: 1.1;">
              ${flagHtml}
              <span style="display: flex; flex-direction: column; justify-content: center;">
                <strong style="font-weight: 700; font-size: 14px; line-height: 1.1;">${name1.trim()}</strong>
                ${nameSubHtml}
              </span>
            </a>
            <div style="flex-shrink: 0; display: flex; align-items: center;">
              <img src="${pluginUrl}/assets/image/sm-windows.png" alt="Windows VPS" style="margin-right: 4px; vertical-align: middle;" />
              <img src="${pluginUrl}/assets/image/sm-linux.png" alt="Linux VPS" style="vertical-align: middle;" />
            </div>
          </div>
          <div class="osp-card-row"><span class="osp-card-label">Core</span><span class="osp-card-value">${plan.core || '-'}</span></div>
          <div class="osp-card-row"><span class="osp-card-label">RAM</span><span class="osp-card-value">${plan.ram || '-'}</span></div>
          <div class="osp-card-row"><span class="osp-card-label">Harddisk</span><span class="osp-card-value">${plan.harddisk || '-'}</span></div>
          <div class="osp-card-row"><span class="osp-card-label">Bandwidth</span><span class="osp-card-value">${plan.bandwidth || '-'}</span></div>
          <div class="osp-card-row"><span class="osp-card-label">DC Location</span><span class="osp-card-value">${plan.location || '-'}</span></div>
          <div class="osp-card-row">
            <span class="osp-card-label">Monthly Price</span>
            <span class="osp-card-value osp-card-value-bold ${hasOffer ? 'strikethrough' : ''}">
              ${formatPrice(plan.monthly_price, currencyInfo, activeCurrency)}
            </span>
          </div>
          ${offerHtml}
          <a href="${plan.buy_url || '#'}" class="osp-btn-configure osp-card-footer-btn">
            <i class="dashicons dashicons-admin-generic"></i> ${buttonText || 'Configure'}
          </a>
        `;

        cardsGrid.appendChild(card);
      });

      contentArea.appendChild(cardsGrid);
    } else {
      // Table / CSS Grid List
      const gridList = document.createElement('div');
      gridList.className = 'osp-grid-list osp-vps-grid f32';

      // Header row
      const headerRow = document.createElement('div');
      headerRow.className = 'osp-list-header';

      const columns = [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'os', label: 'OS', sortable: false },
        { key: 'core', label: 'Core', sortable: true },
        { key: 'ram', label: 'RAM', sortable: true },
        { key: 'harddisk', label: 'Harddisk', sortable: true },
        { key: 'bandwidth', label: 'Bandwidth', sortable: true },
        { key: 'location', label: 'DC Location', sortable: true },
        { key: 'monthly_price', label: 'Monthly Price', sortable: true },
        { key: 'offer_price', label: 'Offer Price', sortable: true },
        { key: 'action', label: 'Action', sortable: false }
      ];

      columns.forEach(col => {
        const colDiv = document.createElement('div');
        let colClass = 'osp-header-col';
        if (col.sortable) {
          colClass += ' sortable';
          if (sortField === col.key) {
            colClass += sortOrder === 'asc' ? ' sort-asc' : ' sort-desc';
          }
          colDiv.addEventListener('click', () => {
            if (sortField === col.key) {
              sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
            } else {
              sortField = col.key;
              sortOrder = 'asc';
            }
            renderListContent();
          });
        }
        colDiv.className = colClass;
        colDiv.textContent = col.label;
        headerRow.appendChild(colDiv);
      });

      gridList.appendChild(headerRow);

      // Body
      const bodyDiv = document.createElement('div');
      bodyDiv.className = 'osp-list-body';

      currentPlans.forEach(plan => {
        const hasOffer = plan.best_offer?.savings > 1 && plan.best_offer?.offer_price > 0;
        const planSlug = slugify(plan.name);
        const randomClasses = generateRandomRowClasses();

        const row = document.createElement('div');
        row.className = `osp-list-row osp-row-pid-${plan.pid} osp-row-name-${planSlug} ${randomClasses}`;

        const flagHtml = plan.flag_class ? `<i class="flag ${plan.flag_class}"></i>` : '';
        const offerColHtml = hasOffer ? `
          <span class="osp-price-discounted">${formatPrice(plan.best_offer.offer_price, currencyInfo, activeCurrency)}</span>
          <span class="osp-price-term">/mo</span>
          <br />
          <span class="osp-savings-badge">Save ${plan.best_offer.savings}%</span>
        ` : '<span class="osp-no-offer">-</span>';

        row.innerHTML = `
          <div class="osp-col-name">
            <a href="${plan.buy_url || '#'}" class="osp-flag-link">
              ${flagHtml}
              ${plan.name}
            </a>
          </div>
          <div class="osp-col-os">
            <img src="${pluginUrl}/assets/image/sm-windows.png" alt="Windows VPS" style="margin-right: 4px;" />
            <img src="${pluginUrl}/assets/image/sm-linux.png" alt="Linux VPS" />
          </div>
          <div class="osp-col-core">${plan.core || '-'}</div>
          <div class="osp-col-ram">${plan.ram || '-'}</div>
          <div class="osp-col-harddisk">${plan.harddisk || '-'}</div>
          <div class="osp-col-bandwidth">${plan.bandwidth || '-'}</div>
          <div class="osp-col-location">${plan.location || '-'}</div>
          <div class="osp-col-price">
            <span class="osp-price-monthly ${hasOffer ? 'strikethrough' : ''}">
              ${formatPrice(plan.monthly_price, currencyInfo, activeCurrency)}
            </span>
          </div>
          <div class="osp-col-offer">${offerColHtml}</div>
          <div class="osp-col-action">
            <a href="${plan.buy_url || '#'}" class="osp-btn-configure">
              <i class="dashicons dashicons-admin-generic"></i> ${buttonText || 'Configure'}
            </a>
          </div>
        `;

        bodyDiv.appendChild(row);
      });

      gridList.appendChild(bodyDiv);
      contentArea.appendChild(gridList);
    }
  };

  const handleResize = () => {
    const newMobile = window.innerWidth <= 768;
    if (newMobile !== isMobile) {
      isMobile = newMobile;
      render();
    }
  };
  window.addEventListener('resize', handleResize);

  const handleCurrencyChange = (e) => {
    if (e.detail && e.detail !== activeCurrency) {
      activeCurrency = e.detail;
      renderListContent();
    }
  };
  window.addEventListener('ospCurrencyChange', handleCurrencyChange);

  render();
}

/**
 * Render Dedicated Server Plans using Custom Vanilla JS
 */
function renderDedicatedApp(el) {
  let plans = [];
  try {
    plans = JSON.parse(el.getAttribute('data-plans') || '[]');
  } catch (e) {
    console.error('Failed to parse Dedicated plans', e);
  }

  let currencyInfo = { inr_rate: 0, euro_rate: 0, show_currency: '1', trace_country: '' };
  try {
    const parsed = JSON.parse(el.getAttribute('data-currency-info') || 'null');
    if (parsed) currencyInfo = parsed;
  } catch (e) {
    console.error('Failed to parse Dedicated currencyInfo', e);
  }

  const type = el.getAttribute('data-type') || '';
  const searchParam = el.getAttribute('data-search') || '';
  const buttonText = el.getAttribute('data-button-text') || 'Configure';
  const initialLayout = (el.getAttribute('data-layout') || 'table').toLowerCase();
  const pagination = (el.getAttribute('data-pagination') || '').toLowerCase() === 'true';
  const searchEnabled = (el.getAttribute('data-search-enabled') || '').toLowerCase() !== 'false';

  let layoutMode = initialLayout === 'box' ? 'box' : 'list';
  let searchQuery = searchParam || '';
  let showSearch = !!searchParam;
  let sortField = 'name';
  let sortOrder = 'asc';
  let currentPage = 1;
  let rowsPerPage = 10;
  let isMobile = window.innerWidth <= 768;
  let activeCurrency = getActiveCurrency(currencyInfo);

  const render = () => {
    el.innerHTML = '';

    const rootDiv = document.createElement('div');
    rootDiv.id = 'osp-sheet';
    rootDiv.className = 'osp-responsive-container f32';

    // Controls bar
    const controlsBar = document.createElement('div');
    controlsBar.className = 'osp-controls-bar';

    if (searchEnabled) {
      const searchContainer = document.createElement('div');
      searchContainer.className = 'osp-search-toggle-container';
      searchContainer.style.cssText = 'display: flex; align-items: center; gap: 8px;';

      if (showSearch) {
        const searchBox = document.createElement('div');
        searchBox.style.cssText = 'display: flex; align-items: center; gap: 8px;';

        const input = document.createElement('input');
        input.type = 'text';
        input.className = 'osp-search-input';
        input.placeholder = 'Search server location ...';
        input.value = searchQuery;
        input.style.cssText = 'width: 200px; padding: 6px 10px; font-size: 13px;';
        input.addEventListener('input', (e) => {
          searchQuery = e.target.value;
          currentPage = 1;
          renderListContent();
        });

        const closeBtn = document.createElement('button');
        closeBtn.type = 'button';
        closeBtn.className = 'osp-search-toggle-btn';
        closeBtn.style.cssText = 'background: transparent; border: none; cursor: pointer; color: #6c757d; padding: 4px; display: flex; align-items: center;';
        closeBtn.title = 'Close Search';
        closeBtn.innerHTML = '<span class="dashicons dashicons-no-alt" style="font-size: 18px; width: 18px; height: 18px;"></span>';
        closeBtn.addEventListener('click', () => {
          showSearch = false;
          searchQuery = '';
          currentPage = 1;
          render();
        });

        searchBox.appendChild(input);
        searchBox.appendChild(closeBtn);
        searchContainer.appendChild(searchBox);
        setTimeout(() => input.focus(), 0);
      } else {
        const toggleBtn = document.createElement('button');
        toggleBtn.type = 'button';
        toggleBtn.className = 'osp-search-toggle-btn';
        toggleBtn.title = 'Search';
        toggleBtn.style.cssText = 'background: #ffffff; border: 1px solid #ccd0d4; border-radius: 6px; cursor: pointer; padding: 6px 10px; display: inline-flex; align-items: center; justify-content: center; transition: all 0.15s ease-in-out; height: 34px; width: 34px;';
        toggleBtn.innerHTML = `
          <svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round" style="width: 16px; height: 16px; display: block; color: #50575e;">
            <circle cx="11" cy="11" r="8"></circle>
            <line x1="21" y1="21" x2="16.65" y2="16.65"></line>
          </svg>
        `;
        toggleBtn.addEventListener('click', () => {
          showSearch = true;
          render();
        });
        searchContainer.appendChild(toggleBtn);
      }
      controlsBar.appendChild(searchContainer);
    }

    const controlsRight = document.createElement('div');
    controlsRight.className = 'osp-controls-right';
    controlsRight.style.cssText = 'display: flex; align-items: center; gap: 15px;';

    if (!isMobile) {
      const layoutSwitcher = document.createElement('div');
      layoutSwitcher.className = 'osp-layout-switcher';

      const listBtn = document.createElement('button');
      listBtn.type = 'button';
      listBtn.className = `osp-layout-btn ${layoutMode === 'list' || layoutMode === 'table' ? 'active' : ''}`;
      listBtn.title = 'List View';
      listBtn.innerHTML = '<span class="dashicons dashicons-list-view"></span>';
      listBtn.addEventListener('click', () => {
        layoutMode = 'list';
        render();
      });

      const boxBtn = document.createElement('button');
      boxBtn.type = 'button';
      boxBtn.className = `osp-layout-btn ${layoutMode === 'box' ? 'active' : ''}`;
      boxBtn.title = 'Grid View';
      boxBtn.innerHTML = '<span class="dashicons dashicons-grid-view"></span>';
      boxBtn.addEventListener('click', () => {
        layoutMode = 'box';
        render();
      });

      layoutSwitcher.appendChild(listBtn);
      layoutSwitcher.appendChild(boxBtn);
      controlsRight.appendChild(layoutSwitcher);
    }

    const switcherEl = createCurrencySwitcherElement(currencyInfo.trace_country, (curr) => {
      activeCurrency = curr;
      renderListContent();
    });
    if (switcherEl) {
      controlsRight.appendChild(switcherEl);
    }

    controlsBar.appendChild(controlsRight);
    rootDiv.appendChild(controlsBar);

    const contentContainer = document.createElement('div');
    contentContainer.className = 'osp-plans-content-area';
    rootDiv.appendChild(contentContainer);

    el.appendChild(rootDiv);

    renderListContent();
  };

  const getFilteredAndSortedPlans = () => {
    const q = searchQuery.toLowerCase().trim();
    const filtered = plans.filter(p => {
      if (type === 'search' || !searchEnabled || !q) return true;
      return (
        (p.name || '').toLowerCase().includes(q) ||
        (p.processor || '').toLowerCase().includes(q) ||
        (p.ram || '').toLowerCase().includes(q) ||
        (p.harddisk || '').toLowerCase().includes(q) ||
        (p.bandwidth || '').toLowerCase().includes(q) ||
        (p.location || '').toLowerCase().includes(q)
      );
    });

    return [...filtered].sort((a, b) => {
      let aVal = a[sortField];
      let bVal = b[sortField];

      if (sortField === 'monthly_price') {
        aVal = parseFloat(a.monthly_price) || 0;
        bVal = parseFloat(b.monthly_price) || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else if (sortField === 'offer_price') {
        aVal = parseFloat(a.best_offer?.offer_price || a.monthly_price) || 0;
        bVal = parseFloat(b.best_offer?.offer_price || b.monthly_price) || 0;
        return sortOrder === 'asc' ? aVal - bVal : bVal - aVal;
      } else {
        const strA = String(aVal || '').replace(/[–—]/g, '-');
        const strB = String(bVal || '').replace(/[–—]/g, '-');
        const cmp = strA.localeCompare(strB, undefined, { numeric: true, sensitivity: 'base' });
        return sortOrder === 'asc' ? cmp : -cmp;
      }
    });
  };

  const renderPaginationBar = (totalRows, totalPages) => {
    if (!pagination || totalPages <= 1) return null;

    const startRow = (currentPage - 1) * rowsPerPage + 1;
    const endRow = Math.min(currentPage * rowsPerPage, totalRows);

    const bar = document.createElement('div');
    bar.className = 'osp-pagination-bar';

    const infoDiv = document.createElement('div');
    infoDiv.innerHTML = `Showing ${startRow} to ${endRow} of ${totalRows} results `;

    const select = document.createElement('select');
    select.className = 'osp-rows-select';
    select.style.marginLeft = '10px';
    [10, 25, 50, 100].forEach(val => {
      const opt = document.createElement('option');
      opt.value = String(val);
      opt.textContent = String(val);
      if (val === rowsPerPage) opt.selected = true;
      select.appendChild(opt);
    });
    select.addEventListener('change', (e) => {
      rowsPerPage = parseInt(e.target.value, 10);
      currentPage = 1;
      renderListContent();
    });
    infoDiv.appendChild(select);
    bar.appendChild(infoDiv);

    const controlsDiv = document.createElement('div');
    controlsDiv.className = 'osp-pagination-controls';

    const addBtn = (text, targetPage, disabled = false, isActive = false) => {
      const btn = document.createElement('button');
      btn.type = 'button';
      btn.className = `osp-page-btn ${isActive ? 'active' : ''}`;
      btn.textContent = text;
      if (disabled) btn.disabled = true;
      btn.addEventListener('click', () => {
        if (!disabled && targetPage !== currentPage) {
          currentPage = targetPage;
          renderListContent();
        }
      });
      controlsDiv.appendChild(btn);
    };

    addBtn('First', 1, currentPage === 1);
    addBtn('Previous', currentPage - 1, currentPage === 1);

    const maxPageButtons = 5;
    const startPage = Math.max(1, currentPage - Math.floor(maxPageButtons / 2));
    const endPage = Math.min(totalPages, startPage + maxPageButtons - 1);

    if (startPage > 1) {
      addBtn('1', 1);
      if (startPage > 2) {
        const dots = document.createElement('span');
        dots.style.padding = '4px';
        dots.textContent = '...';
        controlsDiv.appendChild(dots);
      }
    }

    for (let p = startPage; p <= endPage; p++) {
      addBtn(String(p), p, false, p === currentPage);
    }

    if (endPage < totalPages) {
      if (endPage < totalPages - 1) {
        const dots = document.createElement('span');
        dots.style.padding = '4px';
        dots.textContent = '...';
        controlsDiv.appendChild(dots);
      }
      addBtn(String(totalPages), totalPages);
    }

    addBtn('Next', currentPage + 1, currentPage === totalPages);
    addBtn('Last', totalPages, currentPage === totalPages);

    bar.appendChild(controlsDiv);
    return bar;
  };

  const renderListContent = () => {
    const contentArea = el.querySelector('.osp-plans-content-area');
    if (!contentArea) return;
    contentArea.innerHTML = '';

    const sortedPlans = getFilteredAndSortedPlans();
    const totalRows = sortedPlans.length;
    const totalPages = Math.ceil(totalRows / rowsPerPage) || 1;

    if (currentPage > totalPages) currentPage = totalPages;
    if (currentPage < 1) currentPage = 1;

    const displayPlans = pagination
      ? sortedPlans.slice((currentPage - 1) * rowsPerPage, currentPage * rowsPerPage)
      : sortedPlans;

    const displayAsBox = isMobile || layoutMode === 'box';

    if (displayPlans.length === 0) {
      contentArea.innerHTML = '<div style="padding: 20px; text-align: center; background: #fff; border: 1px solid #dee2e6; border-radius: 8px;">Nothing Found !</div>';
      return;
    }

    if (displayAsBox) {
      const cardsGrid = document.createElement('div');
      cardsGrid.className = 'osp-cards-view osp-vps-cards-grid f32';

      displayPlans.forEach(plan => {
        const hasOffer = plan.best_offer?.savings > 1 && plan.best_offer?.offer_price > 0;
        const planSlug = slugify(plan.name);
        const randomClasses = generateRandomRowClasses();

        const card = document.createElement('div');
        card.className = `osp-card-item osp-card-pid-${plan.pid} osp-card-name-${planSlug} ${randomClasses}`;

        const nameParts = (plan.name || '').split(/\s*[-–—]\s*/);
        const name1 = nameParts[0] || '';
        const name2 = nameParts[1] || '';

        const flagHtml = plan.flag_class ? `<i class="flag ${plan.flag_class}" style="flex-shrink: 0; margin: 0;"></i>` : '';
        const nameSubHtml = name2 ? `<span style="font-size: 11px; color: #6c757d; font-weight: normal; margin-top: 2px; line-height: 1.1;">${name2.trim()}</span>` : '';

        const procParts = (plan.processor || '').split(/\s*[,–—]\s*|\s+-\s+/);
        const procName = procParts[0] || '';
        const procSub = (procParts[2] || procParts[1] || '') ? (procParts[2] || '') + ' /' + (procParts[1] || '') : '';
        const procSubHtml = procSub ? `<span style="display: block; font-size: 10px; color: #6c757d; font-weight: normal;">${procSub.trim()}</span>` : '';

        const offerHtml = hasOffer ? `
          <div class="osp-card-row">
            <span class="osp-card-label">Offer Price</span>
            <span class="osp-card-value osp-card-value-bold" style="color: #28a745;">
              ${formatPrice(plan.best_offer.offer_price, currencyInfo, activeCurrency)}/mo
              <span class="osp-savings-badge">Save ${plan.best_offer.savings}%</span>
            </span>
          </div>
        ` : '';

        card.innerHTML = `
          <div class="osp-card-row" style="border-bottom: 2px solid #dee2e6; padding-bottom: 12px;">
            <a href="${plan.buy_url || '#'}" class="osp-flag-link" style="display: flex; align-items: center; gap: 8px; text-decoration: none; line-height: 1.1;">
              ${flagHtml}
              <span style="display: flex; flex-direction: column; justify-content: center;">
                <strong style="font-weight: 700; font-size: 14px; line-height: 1.1;">${name1.trim()}</strong>
                ${nameSubHtml}
              </span>
            </a>
          </div>
          <div class="osp-card-row">
            <span class="osp-card-label">Processor</span>
            <span class="osp-card-value" style="text-align: right;">
              ${procName.trim()}
              ${procSubHtml}
            </span>
          </div>
          <div class="osp-card-row"><span class="osp-card-label">RAM</span><span class="osp-card-value">${plan.ram || '-'}</span></div>
          <div class="osp-card-row"><span class="osp-card-label">Harddisk</span><span class="osp-card-value">${plan.harddisk || '-'}</span></div>
          <div class="osp-card-row"><span class="osp-card-label">Bandwidth</span><span class="osp-card-value">${plan.bandwidth || '-'}</span></div>
          <div class="osp-card-row"><span class="osp-card-label">IPv4</span><span class="osp-card-value">${plan.ip || '-'}</span></div>
          <div class="osp-card-row"><span class="osp-card-label">Location</span><span class="osp-card-value">${plan.location || '-'}</span></div>
          <div class="osp-card-row">
            <span class="osp-card-label">Monthly Price</span>
            <span class="osp-card-value osp-card-value-bold ${hasOffer ? 'strikethrough' : ''}">
              ${formatPrice(plan.monthly_price, currencyInfo, activeCurrency)}
            </span>
          </div>
          ${offerHtml}
          <a href="${plan.buy_url || '#'}" class="osp-btn-configure osp-card-footer-btn">
            <i class="dashicons dashicons-admin-generic"></i> ${buttonText}
          </a>
        `;

        cardsGrid.appendChild(card);
      });

      contentArea.appendChild(cardsGrid);

      const pagBar = renderPaginationBar(totalRows, totalPages);
      if (pagBar) contentArea.appendChild(pagBar);
    } else {
      // Table / CSS Grid List
      const gridList = document.createElement('div');
      gridList.className = 'osp-grid-list osp-dedicated-grid f32';

      const headerRow = document.createElement('div');
      headerRow.className = 'osp-list-header';

      const columns = [
        { key: 'name', label: 'Name', sortable: true },
        { key: 'processor', label: 'Processor', sortable: true },
        { key: 'ram', label: 'RAM', sortable: true },
        { key: 'harddisk', label: 'Harddisk', sortable: true },
        { key: 'bandwidth', label: 'Bandwidth', sortable: true },
        { key: 'location', label: 'Location', sortable: true },
        { key: 'monthly_price', label: 'Monthly Price', sortable: true },
        { key: 'offer_price', label: 'Offer Price', sortable: true },
        { key: 'action', label: 'Order', sortable: false }
      ];

      columns.forEach(col => {
        const colDiv = document.createElement('div');
        let colClass = 'osp-header-col';
        if (col.sortable) {
          colClass += ' sortable';
          if (sortField === col.key) {
            colClass += sortOrder === 'asc' ? ' sort-asc' : ' sort-desc';
          }
          colDiv.addEventListener('click', () => {
            if (sortField === col.key) {
              sortOrder = sortOrder === 'asc' ? 'desc' : 'asc';
            } else {
              sortField = col.key;
              sortOrder = 'asc';
            }
            currentPage = 1;
            renderListContent();
          });
        }
        colDiv.className = colClass;
        colDiv.textContent = col.label;
        headerRow.appendChild(colDiv);
      });

      gridList.appendChild(headerRow);

      const bodyDiv = document.createElement('div');
      bodyDiv.className = 'osp-list-body';

      displayPlans.forEach(plan => {
        const procParts = (plan.processor || '').split(/\s*[,–—]\s*|\s+-\s+/);
        const procName = procParts[0] || '';
        const procSub = (procParts[2] || procParts[1] || '') ? (procParts[2] || '') + ' /' + (procParts[1] || '') : '';
        const hasOffer = plan.best_offer?.savings > 1 && plan.best_offer?.offer_price > 0;
        const planSlug = slugify(plan.name);
        const randomClasses = generateRandomRowClasses();

        const row = document.createElement('div');
        row.className = `osp-list-row osp-row-pid-${plan.pid} osp-row-name-${planSlug} ${randomClasses}`;

        const flagHtml = plan.flag_class ? `<i class="flag ${plan.flag_class}"></i>` : '';
        const offerColHtml = hasOffer ? `
          <span class="osp-price-discounted">${formatPrice(plan.best_offer.offer_price, currencyInfo, activeCurrency)}</span>
          <span class="osp-price-term">/mo</span>
          <br />
          <span class="osp-savings-badge">Save ${plan.best_offer.savings}%</span>
        ` : '<span class="osp-no-offer">-</span>';

        row.innerHTML = `
          <div class="osp-col-name">
            <a href="${plan.buy_url || '#'}" class="osp-flag-link">
              ${flagHtml}
              ${plan.name}
            </a>
          </div>
          <div class="osp-col-processor">
            ${procName}
            <br />
            <span style="font-size: 11px; color: #6c757d;">${procSub}</span>
          </div>
          <div class="osp-col-ram">${plan.ram || '-'}</div>
          <div class="osp-col-harddisk">${plan.harddisk || '-'}</div>
          <div class="osp-col-bandwidth">${plan.bandwidth || '-'}</div>
          <div class="osp-col-location">${plan.location || '-'}</div>
          <div class="osp-col-price">
            <span class="osp-price-monthly ${hasOffer ? 'strikethrough' : ''}">
              ${formatPrice(plan.monthly_price, currencyInfo, activeCurrency)}
            </span>
          </div>
          <div class="osp-col-offer">${offerColHtml}</div>
          <div class="osp-col-action">
            <a href="${plan.buy_url || '#'}" class="osp-btn-configure">
              <i class="dashicons dashicons-admin-generic"></i> ${buttonText}
            </a>
          </div>
        `;

        bodyDiv.appendChild(row);
      });

      gridList.appendChild(bodyDiv);
      contentArea.appendChild(gridList);

      const pagBar = renderPaginationBar(totalRows, totalPages);
      if (pagBar) contentArea.appendChild(pagBar);
    }
  };

  const handleResize = () => {
    const newMobile = window.innerWidth <= 768;
    if (newMobile !== isMobile) {
      isMobile = newMobile;
      render();
    }
  };
  window.addEventListener('resize', handleResize);

  const handleCurrencyChange = (e) => {
    if (e.detail && e.detail !== activeCurrency) {
      activeCurrency = e.detail;
      renderListContent();
    }
  };
  window.addEventListener('ospCurrencyChange', handleCurrencyChange);

  render();
}

/**
 * Render Other Services (cPanel, SSL, Support, GSuite) using Custom Vanilla JS
 */
function renderOtherApp(el) {
  let plans = [];
  try {
    plans = JSON.parse(el.getAttribute('data-plans') || '[]');
  } catch (e) {
    console.error('Failed to parse other plans', e);
  }

  let currencyInfo = { inr_rate: 0, euro_rate: 0, show_currency: '1', trace_country: '' };
  try {
    const parsed = JSON.parse(el.getAttribute('data-currency-info') || 'null');
    if (parsed) currencyInfo = parsed;
  } catch (e) {
    console.error('Failed to parse other currencyInfo', e);
  }

  let activeCurrency = getActiveCurrency(currencyInfo);
  const pluginUrl = window.ospFrontendData?.pluginUrl || '';

  const render = () => {
    el.innerHTML = '';

    const rootDiv = document.createElement('div');
    rootDiv.id = 'osp-sheet';
    rootDiv.className = 'osp-responsive-container';

    // Controls bar with Currency Switcher aligned right
    const controlsBar = document.createElement('div');
    controlsBar.className = 'osp-controls-bar';
    controlsBar.style.justifyContent = 'flex-end';

    const switcherEl = createCurrencySwitcherElement(currencyInfo.trace_country, (curr) => {
      activeCurrency = curr;
      renderListContent();
    });
    if (switcherEl) {
      controlsBar.appendChild(switcherEl);
    }
    rootDiv.appendChild(controlsBar);

    const contentArea = document.createElement('div');
    contentArea.className = 'osp-plans-content-area';
    rootDiv.appendChild(contentArea);

    el.appendChild(rootDiv);

    renderListContent();
  };

  const renderListContent = () => {
    const contentArea = el.querySelector('.osp-plans-content-area');
    if (!contentArea) return;
    contentArea.innerHTML = '';

    if (plans.length === 0) {
      contentArea.innerHTML = '<div style="padding: 20px; text-align: center; background: #fff; border: 1px solid #dee2e6; border-radius: 8px;">Nothing Found !</div>';
      return;
    }

    const grid = document.createElement('div');
    grid.className = 'osp-other';

    plans.forEach(plan => {
      const hasOffer = plan.best_offer?.savings > 1 && plan.best_offer?.offer_price > 0;
      const sanitizedDescription = DOMPurify.sanitize(plan.description || '');
      const randomClasses = generateRandomRowClasses();

      const item = document.createElement('div');
      item.className = `osp-other-in ${randomClasses}`;

      const iconHtml = plan.icon_name
        ? `<a href="${plan.buy_url || '#'}"><img src="${pluginUrl}/assets/image/${plan.icon_name}.png" alt="${plan.name}" /></a>`
        : '<i class="flag us"></i>';

      const pricingHtml = plan.is_gsuite
        ? `Monthly Price: ${formatPrice(plan.display_price, currencyInfo, activeCurrency)} <em>Monthly</em>`
        : `Monthly Price: ${formatPrice(plan.display_price, currencyInfo, activeCurrency)} <em>${plan.display_cycle || ''}</em>` +
          (hasOffer ? `<br />Offer Price: ${formatPrice(plan.best_offer.offer_price, currencyInfo, activeCurrency)} <em>(Save ${plan.best_offer.savings}%)</em>` : '');

      item.innerHTML = `
        <div class="oth-head">
          <div class="icon">${iconHtml}</div>
          <h3>${plan.name}</h3>
        </div>
        <div class="oth-body">${sanitizedDescription}</div>
        <div class="oth-footer">
          <div class="osp-price">${pricingHtml}</div>
          <a href="${plan.buy_url || '#'}">
            <button class="buy-now" type="button">
              <span class="dashicons dashicons-admin-generic"></span> Configure
            </button>
          </a>
        </div>
      `;

      grid.appendChild(item);
    });

    contentArea.appendChild(grid);
  };

  const handleCurrencyChange = (e) => {
    if (e.detail && e.detail !== activeCurrency) {
      activeCurrency = e.detail;
      renderListContent();
    }
  };
  window.addEventListener('ospCurrencyChange', handleCurrencyChange);

  render();
}

/**
 * Main Initialization Entry Point
 * Can be called with any container element (e.g. document or preview container)
 */
export function initPlans(container = document) {
  if (!container || !container.querySelectorAll) return;

  const mountPoints = container.querySelectorAll('.osp-react-app, .osp-plan-app');
  mountPoints.forEach(el => {
    const appType = el.getAttribute('data-app');
    if (appType === 'currency-switcher') {
      renderCurrencySwitcherApp(el);
    } else if (appType === 'vps') {
      renderVpsApp(el);
    } else if (appType === 'dedicated') {
      renderDedicatedApp(el);
    } else if (appType === 'other') {
      renderOtherApp(el);
    }
  });
}

// Expose to window for global access (e.g. admin live preview or AJAX loaders)
if (typeof window !== 'undefined') {
  window.ospInitPlans = initPlans;
}

// Auto initialize on DOM ready
if (typeof document !== 'undefined') {
  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => initPlans(document));
  } else {
    initPlans(document);
  }
}
